import { Icon } from "./Icons";
import { OSChooser } from "./OSChooser";
import type { Platform } from "../lib/release";
import { displayVersion, artifactFilename } from "../lib/release";
import type { ReleaseState } from "../hooks/use-release";
import type { DetectedOS } from "../lib/os-detection";

type AltHeroProps = {
  chooserOpen: boolean;
  setChooserOpen: (v: boolean) => void;
  onBuyClick: () => void;
  release: ReleaseState;
  detected: DetectedOS;
  onDownload: (platform: Platform) => boolean;
};

// Alt hero - SQL-first dark layout. The only hero variant we ship.
export function AltHero({
  chooserOpen,
  setChooserOpen,
  onBuyClick,
  release,
  detected,
  onDownload,
}: AltHeroProps) {
  const ready = release.status === "ready" && release.manifest !== null;
  const loading = release.status === "loading";
  const unavailable =
    release.status === "unavailable" || release.status === "error";
  const detectedArtifact = ready
    ? release.manifest!.artifacts[detected.platform]
    : undefined;
  const detectedFilename =
    ready && release.manifest
      ? artifactFilename(release.manifest, detected.platform)
      : undefined;

  const handleMainClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!ready || !detectedArtifact) {
      e.preventDefault();
      return;
    }
    const ok = onDownload(detected.platform);
    if (!ok) e.preventDefault();
  };

  const versionSegment = ready
    ? `v ${displayVersion(release.manifest!.version)}`
    : null;
  return (
    <section
      id="hero"
      style={{
        padding: "72px 0 80px",
        background: "var(--ink)",
        color: "#F3F2EC",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,.04) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          pointerEvents: "none",
        }}
      />
      <div className="section-inner" style={{ position: "relative" }}>
        <div
          className="alt-hero-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1.15fr",
            gap: 56,
            alignItems: "center",
          }}
        >
          <div className="alt-hero-copy" style={{ minWidth: 0 }}>
            <div
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: "0.08em",
                color: "rgba(243,242,236,.6)",
                marginBottom: 18,
              }}
            >
              KATENARIA · COPROCESSOR FOR IGNITION
            </div>
            <div style={{ fontSize: 17, color: "#F3F2EC", fontWeight: 600, marginBottom: 14 }}>
              Got a tag script doing too much?
            </div>
            <h1
              className="product-h1"
              style={{
                fontSize: 48,
                lineHeight: 1.05,
                letterSpacing: "-0.03em",
                fontWeight: 500,
                margin: 0,
              }}
            >
              <span className="nowrap">Native signal</span>
              <br />
              <span className="nowrap">processing for Ignition,</span>
              <br />
              <span className="nowrap" style={{ color: "var(--accent)" }}>
                controlled with{" "}
                <span style={{ fontFamily: "var(--font-mono)", fontWeight: 500 }}>SQL</span>.
              </span>
            </h1>
            <p
              style={{
                fontSize: 16,
                color: "rgba(243,242,236,.7)",
                maxWidth: 460,
                marginTop: 18,
                lineHeight: 1.55,
              }}
            >
              A gateway module with a high-performance C++ signal engine. Write SQL pipelines, map
              them to live tags, and publish derived signals back to Ignition.
            </p>
            <div
              className="alt-hero-actions"
              style={{ display: "flex", gap: 12, marginTop: 28, position: "relative" }}
            >
              <div style={{ position: "relative" }}>
                <div
                  className="btn btn-primary btn-xl"
                  style={{
                    padding: 0,
                    overflow: "hidden",
                    opacity: unavailable ? 0.55 : 1,
                  }}
                >
                  <a
                    href={detectedArtifact?.url || "#"}
                    download={detectedFilename}
                    onClick={handleMainClick}
                    aria-disabled={!ready}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "16px 14px 16px 22px",
                      color: "inherit",
                      textDecoration: "none",
                      cursor: ready ? "pointer" : "not-allowed",
                      fontWeight: "inherit",
                    }}
                  >
                    <Icon.Download />
                    Download
                    {loading && (
                      <span
                        aria-hidden
                        style={{
                          width: 12,
                          height: 12,
                          marginLeft: 6,
                          border: "2px solid rgba(0,0,0,.15)",
                          borderTopColor: "rgba(0,0,0,.55)",
                          borderRadius: "50%",
                          animation: "spin 0.8s linear infinite",
                        }}
                      />
                    )}
                  </a>
                  <button
                    type="button"
                    aria-label="Choose platform"
                    onClick={() => setChooserOpen(!chooserOpen)}
                    style={{
                      background: "transparent",
                      border: "none",
                      borderLeft: "1px solid rgba(0,0,0,.15)",
                      padding: "16px 18px 16px 14px",
                      cursor: "pointer",
                      color: "inherit",
                      opacity: 0.7,
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <Icon.Caret />
                  </button>
                </div>
                <OSChooser
                  open={chooserOpen}
                  onDismiss={() => setChooserOpen(false)}
                  detectedPlatform={detected.platform}
                  manifest={release.manifest}
                  onDownload={onDownload}
                />
                {unavailable && (
                  <div
                    className="mono"
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      fontSize: 10,
                      color: "rgba(243,242,236,.55)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {release.status === "error"
                      ? "couldn't load releases — try again later"
                      : "first release pending"}
                  </div>
                )}
              </div>
              <button
                className="btn btn-xl"
                onClick={onBuyClick}
                style={{
                  background: "transparent",
                  color: "#F3F2EC",
                  border: "1px solid rgba(243,242,236,.25)",
                }}
              >
                Buy a license
              </button>
            </div>
            <div
              className="mono"
              style={{
                fontSize: 11,
                color: "rgba(243,242,236,.5)",
                marginTop: 14,
              }}
            >
              {versionSegment ? (
                <span title={release.manifest?.version}>{versionSegment}</span>
              ) : (
                <span style={{ opacity: 0.4 }}>v —.—.—</span>
              )}{" "}
              · native C++ engine · no telemetry · Ignition 8.3.0+
            </div>
          </div>
          <div
            className="alt-hero-code"
            style={{
              minWidth: 0,
              background: "#14110E",
              border: "1px solid rgba(243,242,236,.1)",
              borderRadius: 10,
              padding: 20,
              fontFamily: "var(--font-mono)",
              fontSize: 12.5,
              lineHeight: 1.6,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
                fontSize: 11,
                color: "rgba(243,242,236,.5)",
              }}
            >
              <span
                style={{ width: 10, height: 10, borderRadius: "50%", background: "#E68A1A" }}
              />
              bearing_hpf.sql &nbsp;{" "}
              <span style={{ marginLeft: "auto", color: "rgba(243,242,236,.4)" }}>
                · compiled in 14 ms
              </span>
            </div>
            <pre
              style={{
                margin: 0,
                color: "#E3E1D8",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              <span style={{ color: "#6B6860" }}>{"-- isolate bearing fault frequencies"}</span>
              {"\n"}
              <span style={{ color: "#E68A1A" }}>CREATE MATERIALIZED VIEW</span>{" "}bearing_hpf{" "}
              <span style={{ color: "#E68A1A" }}>AS</span>
              {"\n"}
              <span style={{ color: "#E68A1A" }}>SELECT</span>{" "}device_id,
              {"\n"}
              {"       "}channel_id,
              {"\n"}
              {"       "}
              <span style={{ color: "#8BB4E8" }}>IIR</span>(amplitude,
              {"\n"}
              {"           "}
              <span style={{ color: "#E68A1A" }}>ARRAY</span>[
              <span style={{ color: "#C9A227" }}>0.7265</span>, -
              <span style={{ color: "#C9A227" }}>1.4530</span>,{" "}
              <span style={{ color: "#C9A227" }}>0.7265</span>],
              {"\n"}
              {"           "}
              <span style={{ color: "#E68A1A" }}>ARRAY</span>[
              <span style={{ color: "#C9A227" }}>1.0</span>, -
              <span style={{ color: "#C9A227" }}>1.3725</span>,{" "}
              <span style={{ color: "#C9A227" }}>0.5334</span>]){" "}
              <span style={{ color: "#E68A1A" }}>AS</span> filtered
              {"\n"}
              <span style={{ color: "#E68A1A" }}>FROM</span>{" "}vibration_raw;
              {"\n"}
              {"\n"}
              <span style={{ color: "#6B6860" }}>{"-- alert when signal spikes above rolling baseline"}</span>
              {"\n"}
              <span style={{ color: "#E68A1A" }}>CREATE MATERIALIZED VIEW</span>{" "}bearing_alert{" "}
              <span style={{ color: "#E68A1A" }}>AS</span>
              {"\n"}
              <span style={{ color: "#E68A1A" }}>SELECT</span>{" "}device_id, channel_id, filtered
              {"\n"}
              <span style={{ color: "#E68A1A" }}>FROM</span>{" "}bearing_hpf
              {"\n"}
              <span style={{ color: "#E68A1A" }}>WHERE</span>{" "}filtered &gt;{" "}
              <span style={{ color: "#8BB4E8" }}>MOVING_AVERAGE</span>(filtered,{" "}
              <span style={{ color: "#C9A227" }}>48</span>) *{" "}
              <span style={{ color: "#C9A227" }}>3.0</span>;
              {"\n"}
            </pre>
            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 12,
                fontSize: 11,
                color: "rgba(243,242,236,.5)",
              }}
            >
              <span
                style={{
                  background: "rgba(47,111,78,.2)",
                  color: "#7FC39A",
                  padding: "2px 8px",
                  borderRadius: 999,
                }}
              >
                0 errors · 2 views
              </span>
              <span
                style={{
                  background: "rgba(201,162,39,.15)",
                  color: "#E0C25C",
                  padding: "2px 8px",
                  borderRadius: 999,
                }}
              >
                maps to 14 machines
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
